/**
 * FKX Game Engine — Flappy-style.
 * Physics values are screen-height fractions; applied as px via canvas height.
 * Rendering uses theme/product CDN assets loaded once at boot.
 */
(function () {
  'use strict';

  /* -------------------------------------------------------------------------- */
  /* Physics constants (fractions of viewport height) — DO NOT CHANGE            */
  /* -------------------------------------------------------------------------- */
  /** Downward acceleration in heights / second² */
  var GRAVITY = 1.85;
  /** Instant upward velocity on jump in heights / second (negative = up) */
  var JUMP_FORCE = -0.58;
  /** Max fall speed in heights / second */
  var MAX_VELOCITY = 1.15;
  /** Horizontal scroll speed in widths / second */
  var SCROLL_SPEED = 0.28;
  /** Target frame rate */
  var TARGET_FPS = 60;
  /** Fixed simulation step (seconds) */
  var FIXED_DT = 1 / TARGET_FPS;

  /* Render-only constants (do not affect physics / collisions / score) */
  var PLAYER_MAX_TILT = (25 * Math.PI) / 180;
  var PLAYER_TILT_LERP = 10;
  var BONUS_SPIN_MIN = 15;
  var BONUS_SPIN_MAX = 25;
  var BONUS_BOB_AMP = 0.006;
  var BONUS_BOB_SPEED = 2.4;

  /**
   * Bonus catalog keys. Add one entry here + matching URL in Liquid JSON
   * (#fkx-game-assets.bonuses) to introduce a new bonus type.
   */
  var BONUS_ASSETS = ['shirt', 'hoodie', 'necklace', 'lighter', 'keychain'];

  /* -------------------------------------------------------------------------- */
  /* AssetLoader — load each URL once                                            */
  /* -------------------------------------------------------------------------- */
  function AssetLoader() {
    this.images = {};
  }

  AssetLoader.prototype.loadImage = function (key, url) {
    var self = this;
    return new Promise(function (resolve, reject) {
      if (!url) {
        reject(new Error('Missing asset URL: ' + key));
        return;
      }
      if (self.images[key]) {
        resolve(self.images[key]);
        return;
      }
      var img = new Image();
      img.decoding = 'async';
      img.onload = function () {
        self.images[key] = img;
        resolve(img);
      };
      img.onerror = function () {
        reject(new Error('Failed to load asset: ' + key + ' → ' + url));
      };
      img.src = url;
    });
  };

  AssetLoader.prototype.loadManifest = function (manifest) {
    var self = this;
    var jobs = [];
    if (manifest.player) jobs.push(self.loadImage('player', manifest.player));
    /* Obstacles: placeholder rendering — do not load lighter art for now.
       When swapping to sprites, restore: loadImage('obstacle', manifest.obstacle) */
    var bonuses = manifest.bonuses || {};
    var i;
    var key;
    for (i = 0; i < BONUS_ASSETS.length; i++) {
      key = BONUS_ASSETS[i];
      if (bonuses[key]) jobs.push(self.loadImage('bonus:' + key, bonuses[key]));
    }
    return Promise.all(jobs).then(function () {
      return self;
    });
  };

  AssetLoader.prototype.get = function (key) {
    return this.images[key] || null;
  };

  /* -------------------------------------------------------------------------- */
  /* Loop — fixed-step 60 FPS                                                    */
  /* -------------------------------------------------------------------------- */
  function Loop(onUpdate, onRender) {
    this.onUpdate = onUpdate;
    this.onRender = onRender;
    this._running = false;
    this._raf = 0;
    this._last = 0;
    this._acc = 0;
    this._boundFrame = this._frame.bind(this);
  }

  Loop.prototype.start = function () {
    if (this._running) return;
    this._running = true;
    this._last = performance.now();
    this._acc = 0;
    this._raf = requestAnimationFrame(this._boundFrame);
  };

  Loop.prototype.stop = function () {
    this._running = false;
    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = 0;
    }
  };

  Loop.prototype._frame = function (now) {
    if (!this._running) return;

    var elapsed = (now - this._last) / 1000;
    this._last = now;

    if (elapsed > 0.25) elapsed = 0.25;

    this._acc += elapsed;
    while (this._acc >= FIXED_DT) {
      this.onUpdate(FIXED_DT);
      this._acc -= FIXED_DT;
    }

    this.onRender(this._acc / FIXED_DT);
    this._raf = requestAnimationFrame(this._boundFrame);
  };

  /* -------------------------------------------------------------------------- */
  /* Score                                                                       */
  /* -------------------------------------------------------------------------- */
  function Score() {
    this.value = 0;
  }

  Score.prototype.add = function (amount) {
    this.value += amount || 1;
  };

  Score.prototype.reset = function () {
    this.value = 0;
  };

  /* -------------------------------------------------------------------------- */
  /* Player                                                                      */
  /* -------------------------------------------------------------------------- */
  function Player(game) {
    this.game = game;
    this.x = 0;
    this.y = 0;
    this.radius = 0;
    this.vy = 0;
    this.alive = true;
    /** Visual tilt only — does not affect hitbox */
    this.rotation = 0;
  }

  Player.prototype.reset = function () {
    var h = this.game.height;
    var w = this.game.width;
    this.radius = h * 0.028;
    this.x = w * 0.28;
    this.y = h * 0.45;
    this.vy = 0;
    this.alive = true;
    this.rotation = 0;
  };

  Player.prototype.jump = function () {
    if (!this.alive) return;
    this.vy = JUMP_FORCE * this.game.height;
  };

  Player.prototype.update = function (dt) {
    if (!this.alive) return;

    var h = this.game.height;
    var gravityPx = GRAVITY * h;
    var maxFall = MAX_VELOCITY * h;

    this.vy += gravityPx * dt;
    if (this.vy > maxFall) this.vy = maxFall;

    this.y += this.vy * dt;

    if (this.y - this.radius < 0) {
      this.y = this.radius;
      this.vy = 0;
    }

    if (this.y + this.radius > h) {
      this.y = h - this.radius;
      this.vy = 0;
      this.alive = false;
    }

    /* Smooth visual tilt from velocity (±25°) — render-only */
    var norm = maxFall > 0 ? this.vy / maxFall : 0;
    if (norm < -1) norm = -1;
    if (norm > 1) norm = 1;
    var target = norm * PLAYER_MAX_TILT;
    this.rotation += (target - this.rotation) * Math.min(1, PLAYER_TILT_LERP * dt);
  };

  /* -------------------------------------------------------------------------- */
  /* Obstacle                                                                    */
  /* -------------------------------------------------------------------------- */
  function Obstacle(game, x) {
    this.game = game;
    this.x = x;
    this.width = 0;
    this.gapY = 0;
    this.gapH = 0;
    this.scored = false;
    this.alive = true;
    this._layout();
  }

  Obstacle.prototype._layout = function () {
    var h = this.game.height;
    var w = this.game.width;
    this.width = w * 0.12;
    this.gapH = h * 0.28;
    var margin = h * 0.12;
    var minGap = margin;
    var maxGap = h - margin - this.gapH;
    this.gapY = minGap + Math.random() * Math.max(0.001, maxGap - minGap);
  };

  Obstacle.prototype.update = function (dt) {
    var speed = SCROLL_SPEED * this.game.width;
    this.x -= speed * dt;
    if (this.x + this.width < 0) this.alive = false;
  };

  Obstacle.prototype.topRect = function () {
    return { x: this.x, y: 0, w: this.width, h: this.gapY };
  };

  Obstacle.prototype.bottomRect = function () {
    var y = this.gapY + this.gapH;
    return { x: this.x, y: y, w: this.width, h: this.game.height - y };
  };

  /* -------------------------------------------------------------------------- */
  /* Collectible                                                                 */
  /* -------------------------------------------------------------------------- */
  function Collectible(game, x, y, assetKey) {
    this.game = game;
    this.x = x;
    this.y = y;
    this.baseY = y;
    this.radius = game.height * 0.012;
    this.alive = true;
    this.collected = false;
    this.assetKey = assetKey || BONUS_ASSETS[0];
    this.rotation = Math.random() * Math.PI * 2;
    this.spinSpeed =
      ((BONUS_SPIN_MIN + Math.random() * (BONUS_SPIN_MAX - BONUS_SPIN_MIN)) * Math.PI) / 180;
    this.bobPhase = Math.random() * Math.PI * 2;
    this.bobTime = 0;
  }

  Collectible.prototype.update = function (dt) {
    var speed = SCROLL_SPEED * this.game.width;
    this.x -= speed * dt;
    this.bobTime += dt;
    this.rotation += this.spinSpeed * dt;
    if (this.x + this.radius < 0) this.alive = false;
  };

  /* -------------------------------------------------------------------------- */
  /* Renderer                                                                    */
  /* -------------------------------------------------------------------------- */
  function Renderer(canvas, assets) {
    this.canvas = canvas;
    /* alpha: true — page background shows through (shared theme asset) */
    this.ctx = canvas.getContext('2d', { alpha: true });
    this.dpr = 1;
    this.assets = assets;
  }

  Renderer.prototype.resize = function (cssWidth, cssHeight) {
    var dpr = window.devicePixelRatio || 1;
    if (dpr < 1) dpr = 1;
    if (dpr > 3) dpr = 3;
    this.dpr = dpr;

    var bw = Math.max(1, Math.round(cssWidth * dpr));
    var bh = Math.max(1, Math.round(cssHeight * dpr));
    if (this.canvas.width !== bw) this.canvas.width = bw;
    if (this.canvas.height !== bh) this.canvas.height = bh;
    this.canvas.style.width = cssWidth + 'px';
    this.canvas.style.height = cssHeight + 'px';

    var ctx = this.ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = true;
    if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
  };

  Renderer.prototype.clear = function (width, height) {
    var ctx = this.ctx;
    ctx.clearRect(0, 0, width, height);
  };

  Renderer.prototype._px = function (v) {
    return Math.round(v * this.dpr) / this.dpr;
  };

  Renderer.prototype._drawCenteredImage = function (img, cx, cy, size, rotation) {
    if (!img) return;
    var ctx = this.ctx;
    var aspect = img.naturalWidth / Math.max(1, img.naturalHeight);
    var w = size;
    var h = size;
    if (aspect >= 1) {
      h = size / aspect;
    } else {
      w = size * aspect;
    }
    cx = this._px(cx);
    cy = this._px(cy);
    w = this._px(w);
    h = this._px(h);

    ctx.save();
    ctx.translate(cx, cy);
    if (rotation) ctx.rotate(rotation);
    ctx.drawImage(img, -w * 0.5, -h * 0.5, w, h);
    ctx.restore();
  };

  Renderer.prototype.drawPlayer = function (player) {
    var img = this.assets.get('player');
    var size = player.radius * 2;
    if (img) {
      this._drawCenteredImage(img, player.x, player.y, size, player.rotation);
      return;
    }
    var ctx = this.ctx;
    ctx.save();
    ctx.translate(this._px(player.x), this._px(player.y));
    ctx.rotate(player.rotation || 0);
    ctx.beginPath();
    ctx.fillStyle = '#ffffff';
    ctx.arc(0, 0, player.radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  Renderer.prototype._roundRectPath = function (x, y, w, h, r) {
    var ctx = this.ctx;
    if (r > w * 0.5) r = w * 0.5;
    if (r > h * 0.5) r = h * 0.5;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  };

  /**
   * Temporary obstacle art: white rounded rect + light shadow.
   * Hitbox / dimensions come from obstacle.topRect / bottomRect — unchanged.
   * Later: swap drawObstacle → drawObstacleLighter.
   */
  Renderer.prototype._drawObstaclePlaceholder = function (rect) {
    if (rect.h <= 0 || rect.w <= 0) return;
    var ctx = this.ctx;
    var x = this._px(rect.x);
    var y = this._px(rect.y);
    var w = this._px(rect.w);
    var h = this._px(rect.h);
    var radius = Math.min(w, h) * 0.12;

    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
    ctx.shadowBlur = Math.max(4, w * 0.18);
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = Math.max(2, w * 0.06);
    ctx.fillStyle = '#ffffff';
    this._roundRectPath(x, y, w, h, radius);
    ctx.fill();
    ctx.restore();
  };

  Renderer.prototype.drawObstacle = function (obstacle) {
    this._drawObstaclePlaceholder(obstacle.topRect());
    this._drawObstaclePlaceholder(obstacle.bottomRect());
  };

  /**
   * Future lighter sprite path (not used while placeholders are active).
   * Keep for a one-line swap in drawObstacle.
   */
  Renderer.prototype._drawObstacleHalf = function (img, sx, sy, sw, sh, rect, fromGap) {
    if (rect.h <= 0 || rect.w <= 0 || sh <= 0 || sw <= 0) return;

    var ctx = this.ctx;
    var tileH = rect.w * (sh / sw);
    if (tileH <= 0) return;

    var x = this._px(rect.x);
    var w = this._px(rect.w);
    var tileHp = Math.max(1 / this.dpr, this._px(tileH));
    var maxTiles = Math.ceil(rect.h / tileH) + 2;
    var i;
    var y;

    if (fromGap) {
      y = rect.y + rect.h - tileH;
      for (i = 0; i < maxTiles; i++) {
        ctx.drawImage(img, sx, sy, sw, sh, x, this._px(y), w, tileHp);
        y -= tileH;
        if (y + tileH <= rect.y) break;
      }
    } else {
      y = rect.y;
      for (i = 0; i < maxTiles; i++) {
        ctx.drawImage(img, sx, sy, sw, sh, x, this._px(y), w, tileHp);
        y += tileH;
        if (y >= rect.y + rect.h) break;
      }
    }
  };

  Renderer.prototype.drawObstacleLighter = function (obstacle) {
    var img = this.assets.get('obstacle');
    var top = obstacle.topRect();
    var bottom = obstacle.bottomRect();
    var ctx = this.ctx;

    if (!img || !img.naturalWidth) {
      this._drawObstaclePlaceholder(top);
      this._drawObstaclePlaceholder(bottom);
      return;
    }

    var sw = img.naturalWidth;
    var sh = img.naturalHeight;
    var half = (sh / 2) | 0;
    var bottomH = sh - half;

    ctx.save();
    ctx.beginPath();
    ctx.rect(top.x, top.y, top.w, top.h);
    ctx.clip();
    this._drawObstacleHalf(img, 0, 0, sw, half, top, true);
    ctx.restore();

    ctx.save();
    ctx.beginPath();
    ctx.rect(bottom.x, bottom.y, bottom.w, bottom.h);
    ctx.clip();
    this._drawObstacleHalf(img, 0, half, sw, bottomH, bottom, false);
    ctx.restore();
  };

  Renderer.prototype.drawCollectible = function (item) {
    if (item.collected) return;
    var img = this.assets.get('bonus:' + item.assetKey);
    var bob =
      Math.sin(item.bobTime * BONUS_BOB_SPEED + item.bobPhase) *
      (this.canvas.height / this.dpr) *
      BONUS_BOB_AMP;
    var drawY = item.y + bob;
    var size = item.radius * 2.8;
    if (img) {
      this._drawCenteredImage(img, item.x, drawY, size, item.rotation);
      return;
    }
    var ctx = this.ctx;
    ctx.beginPath();
    ctx.fillStyle = '#ffe566';
    ctx.arc(this._px(item.x), this._px(drawY), item.radius, 0, Math.PI * 2);
    ctx.fill();
  };

  Renderer.prototype.drawScore = function (score, width) {
    var ctx = this.ctx;
    var size = Math.max(16, this.canvas.height / this.dpr * 0.045);
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.font = '600 ' + size + 'px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(String(score.value), width * 0.5, size * 0.6);
  };

  /* -------------------------------------------------------------------------- */
  /* Game                                                                        */
  /* -------------------------------------------------------------------------- */
  function Game(canvas, assets) {
    this.canvas = canvas;
    this.width = 0;
    this.height = 0;
    this.assets = assets;
    this.renderer = new Renderer(canvas, assets);
    this.player = new Player(this);
    this.score = new Score();
    this.obstacles = [];
    this.collectibles = [];
    this.loop = new Loop(this.update.bind(this), this.render.bind(this));
    this._spawnTimer = 0;
    this._spawnEvery = 1.55;
    this._boundResize = this.resize.bind(this);
    this._boundInput = this._onInput.bind(this);
    this._boundKey = this._onKey.bind(this);
    this._running = false;
  }

  Game.prototype.resize = function () {
    var parent = this.canvas.parentElement || document.documentElement;
    var w = parent.clientWidth || window.innerWidth;
    var h = parent.clientHeight || window.innerHeight;
    var prevW = this.width || w;
    var prevH = this.height || h;

    this.width = w;
    this.height = h;
    this.renderer.resize(w, h);

    if (!this._booted) {
      this.player.reset();
      this._booted = true;
      return;
    }

    if (prevW === w && prevH === h) return;

    var sx = prevW ? w / prevW : 1;
    var sy = prevH ? h / prevH : 1;

    /* Preserve world state — only rescale into the new viewport */
    this.player.x *= sx;
    this.player.y *= sy;
    this.player.vy *= sy;
    this.player.radius = h * 0.028;
    this.player.x = Math.min(Math.max(this.player.x, this.player.radius), w - this.player.radius);
    this.player.y = Math.min(Math.max(this.player.y, this.player.radius), h - this.player.radius);

    var i;
    var obs;
    for (i = 0; i < this.obstacles.length; i++) {
      obs = this.obstacles[i];
      obs.x *= sx;
      obs.width = w * 0.12;
      obs.gapY *= sy;
      obs.gapH *= sy;
    }

    var item;
    for (i = 0; i < this.collectibles.length; i++) {
      item = this.collectibles[i];
      item.x *= sx;
      item.y *= sy;
      item.baseY = item.y;
      item.radius = h * 0.012;
    }
  };

  Game.prototype.start = function () {
    if (this._running) return;
    this._running = true;
    this.resize();
    this.resetWorld();
    this._bindInput();
    window.addEventListener('resize', this._boundResize, { passive: true });
    window.addEventListener('orientationchange', this._boundResize, { passive: true });
    this.loop.start();
  };

  Game.prototype.stop = function () {
    this._running = false;
    this.loop.stop();
    this._unbindInput();
    window.removeEventListener('resize', this._boundResize);
    window.removeEventListener('orientationchange', this._boundResize);
  };

  Game.prototype.resetWorld = function () {
    this.score.reset();
    this.obstacles.length = 0;
    this.collectibles.length = 0;
    this._spawnTimer = 0;
    this.player.reset();
    this._spawnPair(this.width * 1.05);
  };

  Game.prototype._bindInput = function () {
    window.addEventListener('keydown', this._boundKey, { passive: false });
    this.canvas.addEventListener('pointerdown', this._boundInput, { passive: false });
  };

  Game.prototype._unbindInput = function () {
    window.removeEventListener('keydown', this._boundKey);
    this.canvas.removeEventListener('pointerdown', this._boundInput);
  };

  Game.prototype._onKey = function (event) {
    if (event.code !== 'Space' && event.key !== ' ') return;
    event.preventDefault();
    if (event.repeat) return;
    this._flap();
  };

  Game.prototype._onInput = function (event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    this._flap();
  };

  Game.prototype._flap = function () {
    if (!this.player.alive) {
      this.resetWorld();
      return;
    }
    this.player.jump();
  };

  Game.prototype._pickBonusKey = function () {
    var available = [];
    var i;
    var key;
    for (i = 0; i < BONUS_ASSETS.length; i++) {
      key = BONUS_ASSETS[i];
      if (this.assets.get('bonus:' + key)) available.push(key);
    }
    if (!available.length) return BONUS_ASSETS[0];
    return available[Math.floor(Math.random() * available.length)];
  };

  Game.prototype._spawnPair = function (x) {
    var obstacle = new Obstacle(this, x);
    this.obstacles.push(obstacle);
    var cx = obstacle.x + obstacle.width * 0.5;
    var cy = obstacle.gapY + obstacle.gapH * 0.5;
    this.collectibles.push(new Collectible(this, cx, cy, this._pickBonusKey()));
  };

  Game.prototype.update = function (dt) {
    this.player.update(dt);

    var i;
    for (i = this.obstacles.length - 1; i >= 0; i--) {
      this.obstacles[i].update(dt);
      if (!this.obstacles[i].alive) this.obstacles.splice(i, 1);
    }

    for (i = this.collectibles.length - 1; i >= 0; i--) {
      this.collectibles[i].update(dt);
      if (!this.collectibles[i].alive) this.collectibles.splice(i, 1);
    }

    this._spawnTimer += dt;
    if (this._spawnTimer >= this._spawnEvery) {
      this._spawnTimer = 0;
      this._spawnPair(this.width + this.width * 0.08);
    }

    this._resolveCollisions();
  };

  Game.prototype._circleRect = function (cx, cy, r, rect) {
    var nearestX = Math.max(rect.x, Math.min(cx, rect.x + rect.w));
    var nearestY = Math.max(rect.y, Math.min(cy, rect.y + rect.h));
    var dx = cx - nearestX;
    var dy = cy - nearestY;
    return dx * dx + dy * dy <= r * r;
  };

  Game.prototype._resolveCollisions = function () {
    var p = this.player;
    if (!p.alive) return;

    var i;
    var obs;
    for (i = 0; i < this.obstacles.length; i++) {
      obs = this.obstacles[i];
      if (
        this._circleRect(p.x, p.y, p.radius, obs.topRect()) ||
        this._circleRect(p.x, p.y, p.radius, obs.bottomRect())
      ) {
        p.alive = false;
        return;
      }

      if (!obs.scored && obs.x + obs.width < p.x) {
        obs.scored = true;
        this.score.add(1);
      }
    }

    var item;
    var dx;
    var dy;
    var rr;
    for (i = 0; i < this.collectibles.length; i++) {
      item = this.collectibles[i];
      if (item.collected) continue;
      dx = p.x - item.x;
      dy = p.y - item.y;
      rr = p.radius + item.radius;
      if (dx * dx + dy * dy <= rr * rr) {
        item.collected = true;
        item.alive = false;
        this.score.add(1);
      }
    }
  };

  Game.prototype.render = function () {
    this.renderer.clear(this.width, this.height);

    var i;
    for (i = 0; i < this.obstacles.length; i++) {
      this.renderer.drawObstacle(this.obstacles[i]);
    }
    for (i = 0; i < this.collectibles.length; i++) {
      this.renderer.drawCollectible(this.collectibles[i]);
    }
    this.renderer.drawPlayer(this.player);
    this.renderer.drawScore(this.score, this.width);
  };

  /* -------------------------------------------------------------------------- */
  /* Boot                                                                        */
  /* -------------------------------------------------------------------------- */
  function readManifest() {
    var el = document.getElementById('fkx-game-assets');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('[FKX Game] Invalid asset manifest', err);
      return null;
    }
  }

  function boot() {
    var canvas = document.getElementById('fkx-game-canvas');
    if (!canvas) return;

    var manifest = readManifest();
    if (!manifest) {
      console.error('[FKX Game] Missing #fkx-game-assets manifest');
      return;
    }

    var loader = new AssetLoader();
    loader
      .loadManifest(manifest)
      .then(function (assets) {
        var game = new Game(canvas, assets);
        game.start();
        window.FKXGame = game;
      })
      .catch(function (err) {
        console.error('[FKX Game] Asset load failed', err);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
